import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'antd';
import { get } from 'lodash';
import { useAtomValue } from 'jotai';
import { LoadingOutlined } from '@ant-design/icons';
import { METRIC_POPUP_OK_TEXT, isAppModule, PRODUCT_MODE_MAP } from '@utils/constants';
import { SPI_LABEL } from '@components/top-widget-panel/spi-panel/widgets/constants';
import { isAppSPIEnabledSelector } from '@store/common/selectors';
import { useRefTags } from '@src/hooks';
import { tid } from '@utils/test';
import ModalTitleWithSearch from './ModalTitleSearch';
import useMetricCategoryOrder from './hooks/useMetricCategoryOrder';
import useMetricCategory from './hooks/useMetricCategory';
import useTemplateMetrics from './hooks/useTemplateMetrics';
import CheckboxGroup from './CheckboxGroup';
import ModalAnchor from './MetricAnchor';
import {
  RowWrapper,
  ContentWrapper,
  RightPanel,
  FooterWrapper,
  CountText,
  FlexFill,
  StyledDrawer,
  StyledLeftPanel,
  StyledSpin,
} from './styled';
import { contextTypeAtom } from '@src/atoms/page';
import NewCustomMetricBox from './components/NewCustomMetricBox';

const MetricPopupModal = ({
  title,
  visible,
  onCancel,
  onOk,
  okText = METRIC_POPUP_OK_TEXT,
  onClear,
  selectedMetricList,
  applyKpiMutation,
  metricCategories,
  onChange,
  tempKpi,
  setTempKpi,
  onBulkChange,
  searchRef,
  onActivate,
  onCreate,
  isLoading = false,
}) => {
  const contextType = useAtomValue(contextTypeAtom);
  const isAppSPIEnabled = useSelector(isAppSPIEnabledSelector);
  const metricCategoryOrder = useMetricCategoryOrder({ selectedMetricList });
  const metricCategory = useMetricCategory({ selectedMetricList });
  const currentMetricCategoryOrder = metricCategoryOrder[contextType];
  const currentMetricCategory = metricCategory[contextType];
  const { refs, setRefFn } = useRefTags(currentMetricCategoryOrder);
  const [searchText, setSearchText] = useState('');
  const { templateMetricCategory, templateMetricCategories } = useTemplateMetrics({
    selectedMetricList,
  });
  const isAppContext = isAppModule || contextType === PRODUCT_MODE_MAP.APP;

  // group level checkbox state
  const groupChecked = useMemo(() => {
    const groupState = {};
    for (const categoryKey in currentMetricCategory) {
      const inputMetricList = get(metricCategories, [currentMetricCategory[categoryKey]], []);
      const groupChecked = inputMetricList.length > 0 && inputMetricList.every((m) => m.checked);
      groupState[categoryKey] = groupChecked;
    }
    return groupState;
  }, [metricCategories]);

  const onCancelModal = () => {
    setSearchText('');
    onCancel();
  };

  const getInputListFn = (metricsList: any = []) => {
    if (searchText) {
      const st = searchText.toLowerCase();
      return metricsList.filter((metric) => metric.name.toLowerCase().includes(st));
    }
    return metricsList;
  };

  const handleActivate = (categoryKey: string) => {
    const useCaseId = templateMetricCategories[categoryKey]?.[0]?.template?.useCaseId;
    onActivate?.(useCaseId);
  };

  const metricCount = selectedMetricList.length;
  const footerButtons = [
    <FooterWrapper key="footer-wrapper" {...tid('footer')}>
      <Button key="clear" onClick={onClear}>
        Clear All
      </Button>
      <CountText>{`${metricCount} ${metricCount > 1 ? 'metrics' : 'metric'} selected`}</CountText>
      <FlexFill />
      <Button key="discard" onClick={onCancelModal}>
        Cancel
      </Button>
      <Button
        type="primary"
        key="save"
        icon={applyKpiMutation.isLoading ? <LoadingOutlined /> : null}
        onClick={onOk}
      >
        {okText}
      </Button>
    </FooterWrapper>,
  ];

  const titleWithSearch = (
    <ModalTitleWithSearch
      placeholder="Search metric"
      modalTitle={title}
      searchText={searchText}
      setSearchText={setSearchText}
      searchRef={searchRef}
    />
  );

  // skip excluded categories
  const categoryOrder = currentMetricCategoryOrder.filter(
    (categoryKey) => currentMetricCategory[categoryKey] in metricCategories,
  );

  return (
    <StyledDrawer
      title={titleWithSearch}
      open={visible}
      onClose={onCancelModal}
      footer={footerButtons}
      width={1024}
      {...tid('metric-popup')}
    >
      <ContentWrapper {...tid('content')}>
        {isLoading ? (
          <StyledSpin spinning={isLoading} />
        ) : (
          <>
            <StyledLeftPanel>
              <ModalAnchor
                navRefs={refs.current}
                categoryOrder={categoryOrder}
                listCategory={metricCategory}
                isAppSPIEnabled={isAppSPIEnabled}
              />
              {isAppContext && <NewCustomMetricBox onCreate={onCreate} />}
            </StyledLeftPanel>
            <RightPanel>
              {categoryOrder.map((categoryKey) => {
                if (isAppContext && categoryKey === SPI_LABEL.APP && !isAppSPIEnabled) {
                  return null;
                }
                let isTemplate = false;
                if (isAppContext && templateMetricCategory.includes(categoryKey)) {
                  isTemplate = true;
                }
                const isUserDefined = isAppContext && categoryKey === 'USER_DEFINED_METRICS';

                return (
                  <RowWrapper key={categoryKey}>
                    <CheckboxGroup
                      categoryKey={categoryKey}
                      listCategory={metricCategory}
                      inputList={getInputListFn(
                        metricCategories[currentMetricCategory[categoryKey]],
                      )}
                      onChange={onChange}
                      onBulkChange={onBulkChange}
                      setRef={setRefFn(categoryKey)}
                      setTempKpi={setTempKpi}
                      tempKpi={tempKpi}
                      groupChecked={groupChecked}
                      hasSearchText={!!searchText}
                      emptyText={<>No metrics available</>}
                      onActivate={handleActivate}
                      isTemplate={isTemplate}
                      isUserDefined={isUserDefined}
                      contentType={'metric'}
                    />
                  </RowWrapper>
                );
              })}
            </RightPanel>
          </>
        )}
      </ContentWrapper>
    </StyledDrawer>
  );
};

export default MetricPopupModal;
